import React from 'react';
import { Box } from '@mui/material';
import FieldRow from './FieldRow';
import LocalizedField from './LocalizedField';
import SectionBlock from './SectionBlock';
import { SCHEDULE_TYPE_LABEL } from '../../../constants/gameSchedule';
import { fmtLocalDate } from '../../../utils/dateUtils';

const bySortOrder = (a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);

// The text of a localized value for one line: English when there is one, else the first language.
export const primaryText = (text) => {
  const values = Array.isArray(text) ? text : (Array.isArray(text?.values) ? text.values : []);
  return (values.find((v) => v?.localeId === 'en_us') ?? values[0])?.value ?? null;
};

/**
 * A scheduled quiz for the approver (create: the whole new quiz; delete: the quiz being removed): its period and every
 * question with its options, the correct one marked and also named at the end of the question. Everything is shown by
 * name and value, never by id, and questions and options are labelled by position. The approver is the only person who
 * sees the correct answers besides the marketing team: the published quiz never carries them.
 */
export default function QuizDetailPanel({ data, diffKeys = new Set(), side }) {
  if (!data) return null;

  const type = String(data.scheduleType ?? '').toLowerCase();
  const questions = Array.isArray(data.questions) ? [...data.questions].sort(bySortOrder) : [];

  return (
    <Box>
      <SectionBlock title="Quiz" />
      <FieldRow label="Name" value={data.name} isChanged={diffKeys.has('name')} side={side} />
      <FieldRow label="Period" value={SCHEDULE_TYPE_LABEL[type] ?? data.scheduleType} isChanged={diffKeys.has('scheduleType')} side={side} />
      <FieldRow label="Start Date" value={fmtLocalDate(data.startDate)} isChanged={diffKeys.has('startDate')} side={side} />
      <FieldRow label="End Date" value={fmtLocalDate(data.endDate)} isChanged={diffKeys.has('endDate')} side={side} />
      <FieldRow label="Questions" value={String(questions.length)} side={side} />

      {data.linkedAnnouncement && (
        <>
          <SectionBlock title="Linked announcement" />
          <FieldRow label="Announcement" value={data.linkedAnnouncement.name} side={side} />
          {data.linkedAnnouncement.quizCount > 1 && (
            <FieldRow label="Quizzes in it" value={String(data.linkedAnnouncement.quizCount)} side={side} />
          )}
          <FieldRow label="Start Date" value={fmtLocalDate(data.linkedAnnouncement.startDate)} side={side} />
          <FieldRow label="End Date" value={fmtLocalDate(data.linkedAnnouncement.endDate)} side={side} />
          <FieldRow label="Announcement Date" value={fmtLocalDate(data.linkedAnnouncement.announcementDate)} side={side} />
        </>
      )}

      {questions.map((q, qi) => {
        const options = Array.isArray(q.options) ? [...q.options].sort(bySortOrder) : [];
        const correct = options.find((o) => o.isCorrect);
        return (
          <React.Fragment key={q.id ?? `q-${qi}`}>
            <SectionBlock title={`Question ${qi + 1}`} />
            <LocalizedField label="Question" data={q.text} side={side} />
            {options.map((o, oi) => (
              <LocalizedField
                key={o.id ?? `o-${qi}-${oi}`}
                label={`Option ${oi + 1}${o.isCorrect ? ' ✓' : ''}`}
                data={o.text}
                side={side}
              />
            ))}
            <FieldRow label="Correct answer" value={correct ? primaryText(correct.text) : '— none marked —'} side={side} />
          </React.Fragment>
        );
      })}
    </Box>
  );
}
